import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { switchMap } from 'rxjs/operators';

import {
  Location,
  LocationRequest,
  LocationUpdateStatusRequest,
  LocationUpdateLogoRequest
} from '../models/location.model';

@Injectable({
  providedIn: 'root'
})
export class LocationService {
  private apiUrl = `${environment.apiURL}`;

  constructor(private http: HttpClient) { }

  get baseApiUrl(): string {
    return this.apiUrl;
  }


  // Get all locations by customer ID
  getLocationsByCustomerId(customerId: string): Observable<Location[]> {
    return this.http.get<Location[]>(`${this.apiUrl}/location/list/${customerId}`);
  }

  // Check if location is associated with devices
  checkAssociatedWithDevices(locationId: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/location/checkAssociatedwithDevices/${locationId}`);
  }

  // Get location by ID
  getLocationById(locationId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/location/get/${locationId}`);
  }

  // Create new location
  createLocation(locationRequest: LocationRequest): Observable<string> {
    return this.http.post<string>(`${this.apiUrl}/location/add`, locationRequest);
  }

  // Update existing location
  updateLocation(locationId: string, locationRequest: LocationRequest): Observable<string> {
    return this.http.put<string>(`${this.apiUrl}/location/edit/${locationId}`, locationRequest);
  }

  // Delete location
  deleteLocation(locationId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.apiUrl}/location/delete/${locationId}`);
  }

  // Update location status (Active/Inactive)
  updateLocationStatus(locationId: string, statusRequest: LocationUpdateStatusRequest): Observable<string> {
    return this.http.patch<string>(`${this.apiUrl}/location/status/${locationId}`, statusRequest);
  }

  // Update location logo
  updateLocationLogo(locationId: string, logoRequest: LocationUpdateLogoRequest): Observable<string> {
    return this.http.patch<string>(`${this.apiUrl}/location/logo/${locationId}`, logoRequest);
  }

  getImageAsBase64(url: string): Observable<string> {
    return this.http.get(url, { headers: new HttpHeaders({ 'X-Skip-Auth': 'true' }), responseType: 'blob' }).pipe(
      switchMap(blob => this.convertBlobToBase64(blob))
    );
  }

  private convertBlobToBase64(blob: Blob): Observable<string> {
    return new Observable(observer => {
      const reader = new FileReader();
      reader.onloadend = () => {
        observer.next(reader.result as string);
        observer.complete();
      };
      reader.readAsDataURL(blob);
    });
  }

  // Reassign location to customer
  reassignLocation(payload: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/location/reassign`, payload);
  }

  getLocationsByCustomerIds(customerIds: string[]): Observable<any[]> {
    return this.http.post<any[]>(`${this.apiUrl}/location/locations`, customerIds);
  }

  getLocationsDetailsByCustomerIds(customerIds: string[]): Observable<any[]> {
    return this.http.post<any[]>(`${this.apiUrl}/location/getLocationsDetails`, customerIds);
  }

  getBuildingsByLocationIds(locationIds: string[]): Observable<any[]> {
    return this.http.post<any[]>(`${this.apiUrl}/location/buildings`, locationIds);
  }

  getFloorsByBuildingIds(buildingIds: string[]): Observable<any[]> {
    return this.http.post<any[]>(`${this.apiUrl}/location/buildings/floors`, buildingIds);
  }

  getDepartmentsByFloorIds(floorIds: string[]): Observable<any[]> {
    return this.http.post<any[]>(`${this.apiUrl}/location/floors/departments`, floorIds);
  }

  // Get locations for current logged-in user based on their role and permissions
  // No customerId parameter required - automatically determines accessible locations

  getMyLocations(): Observable<Location[]> {
    return this.http.get<Location[]>(`${this.apiUrl}/location/mylocations`);
  }

  // Get weather data for a location
  getWeatherByLocationId(locationId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/location/weather/${locationId}`);
  }
}